import React, { FocusEvent, FunctionComponent, InputHTMLAttributes, useRef, useState } from 'react';
import { Input } from 'form/components';
import { Droplist, Icon } from 'lmc-ui-library';
import { DroplistContainer, SearchInputButton, SearchInputContainer } from './Search.styled';
import { Option as DroplistOption } from 'lmc-ui-library/lib/components/atoms/Droplist/Droplist.types';
import useOutsideClick from 'utils/useOutsideClick';
import { FormInput } from 'form/FormInput';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  Component?: FunctionComponent<InputHTMLAttributes<HTMLInputElement> & FormInput>;
  onOptionClick: (option: Option, key: number) => void;
  onSearchClick?: () => void;
  onSearchInputChange?: (value: string) => void;
  options: Option[];
  state?: FormInput['state'];
  containerWidth?: string;
  fullWidth?: boolean;
  newUi?: boolean;
}

export interface Option extends DroplistOption {
  selectedLabel?: string;
  selectedRender?: any;
  params?: any;
}

const Search = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      Component,
      onSearchClick,
      onOptionClick,
      onSearchInputChange,
      options,
      state,
      containerWidth,
      fullWidth,
      ...props
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isFocused, setFocused] = useState<boolean>(false);

    const droplistOptions = options.map((option) => ({
      ...option,
      selected: false,
    }));

    useOutsideClick(containerRef, () => {
      setFocused(false);
    });

    const handleInputFocus = (e: FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      if (props.onFocus) {
        props.onFocus(e);
      }
    };

    const handleOptionClick = (option: Option, key: number) => {
      if (onOptionClick) {
        onOptionClick(option, key);
      }
      setFocused(false);
    };

    const InputComponent: FunctionComponent<InputHTMLAttributes<HTMLInputElement> & FormInput> = Component
      ? Component
      : Input;

    return (
      <SearchInputContainer containerWidth={containerWidth} fullWidth={fullWidth} ref={containerRef}>
        <InputComponent
          {...props}
          state={state}
          onFocus={handleInputFocus}
          onFocusCapture={handleInputFocus}
          onChange={(e) => {
            const value = e.target.value;
            if (onSearchInputChange) {
              onSearchInputChange(value);
              setFocused(false);
            }

            if (props.onChange) {
              props.onChange(e);
              setFocused(false);
            }
          }}
        />

        <SearchInputButton
          newUi={props.newUi}
          type="button"
          mode="primary"
          borderVariant="rectangle"
          small={false}
          onClick={() => {
            setFocused(true);
            if (onSearchClick) {
              onSearchClick();
            }
          }}>
          <Icon type="search" color="#ffffff" />
          <span>Search</span>
        </SearchInputButton>
        {isFocused && Boolean(droplistOptions.length) && (
          <DroplistContainer>
            <Droplist options={droplistOptions} onOptionClick={handleOptionClick} />
          </DroplistContainer>
        )}
      </SearchInputContainer>
    );
  },
);

export default Search;
